import { Fragment, useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef, memo } from 'react';
import { Button, Layout, Card, Form, Input, Select, Tooltip, Row, Col, Divider, Modal, Checkbox, Radio, message, Switch } from 'antd';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import './index.less';
import Choiceitem from './Choiceitem';
import Morecloze from './Morecloze';
import Answer from './Answer';
import Fill from './Fill';
import Judge from './Judge';
import Region from './Region';
import Sort from './Sort';
import Matrix from './Matrix';
import UploadImage from './UploadImage';
import Quantization from './Quantization';
import Elsign from './Elsign';
import SelectQuestion from './SelectQuestion';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { previewTemplateApi, timesaveTemplateQuestionApi, saveTemplateQuestionApi, changeTypeApi, delTemplateQuestionApi, sortTemplateQuestionApi } from '@services/Wj/Template';
import { getQuestionBankListApi, getAllLogicApi } from '@services/Wj';
import { previewSurveryApi, delAnswerApi, saveSurveryQuestionApi, timesaveSurveryQuestionApi, delSurveryQusetionApi, sortSurveryQuestionApi, changeSurveyTypeApi, subSurveyApi } from '@services/Wj/Question';
import RichEditor from '@components/common/RichEditor';
import { useSurveyAutosave } from '../SurveyAutosaveContext';
import { applyQuestionStemFocusBorder, clearQuestionStemFocusBorder } from './richEditorFocusHelpers';

const id = 0;

const getQuestionItemKey = (item, index) => {
    if (item?.id != null) {
        return `question-${item.id}`;
    }
    if (item?.weight != null) {
        return `question-temp-${item.weight}-${item.questionTypeId ?? 'unknown'}`;
    }
    return `question-fallback-${index}`;
};

/** 去掉 dnd-kit 排序动画中的 scale（useDerivedTransform 会算 scaleX/scaleY），避免拖动时卡片被拉伸变形 */
const normalizeSortableTransform = transform => {
    if (!transform) {
        return null;
    }
    return {
        ...transform,
        scaleX: 1,
        scaleY: 1,
    };
};

const QuestionContent = memo(function QuestionContent({ item, anserArea, setAnserArea, logicList, onReset, selectQuestionItem, moreRef, start, end }) {
    return (
        <div>
            {item.questionTypeId === 1 && <Choiceitem questionId={`questionId-${id}`} type="single" item={item} anserArea={anserArea} setAnserArea={setAnserArea} logicList={logicList} onReset={onReset} />}
            {item.questionTypeId === 2 && <Choiceitem questionId={`questionId-${id}`} type="multiple" item={item} anserArea={anserArea} setAnserArea={setAnserArea} logicList={logicList} onReset={onReset} />}
            {item.questionTypeId === 3 && <Judge questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 4 && <Fill questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} onStemFocus={() => selectQuestionItem(item)} />}
            {item.questionTypeId === 5 && <Morecloze questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} ref={moreRef} start={start} end={end} onReset={onReset} />}
            {item.questionTypeId === 6 && <Answer questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 7 && <Region questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 8 && <Quantization questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 9 && <Sort questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 10 && <Matrix questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 11 && <UploadImage questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 12 && <Elsign questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
            {item.questionTypeId === 13 && <SelectQuestion questionId={`questionId-${id}`} item={item} anserArea={anserArea} setAnserArea={setAnserArea} onReset={onReset} />}
        </div>
    );
});

const QuestionCard = memo(function QuestionCard({
    item,
    anserArea,
    setAnserArea,
    logicList,
    onReset,
    onQuestionClick,
    selectQuestionItem,
    onRequire,
    handleChange,
    typechange,
    location,
    onSelectMultipleChoice,
    changeOpinion,
    onChangeSelect,
    goLogic,
    onCopy,
    onDelete,
    moreRef,
    start,
    end,
    dragHandleListeners,
    dragHandleAttributes,
    isDragging,
    isSelected,
}) {
    return (
        <div className={`main_item_wrap${isDragging ? ' main_item_wrap--dragging' : ''}${isSelected ? ' main_item_wrap--selected' : ''}`} id={`question-box-${item.id}`} onClick={e => onQuestionClick(e, item, 'in')}>
            <div className={`question-drag-backdrop${isDragging ? ' question-drag-backdrop--dragging' : ''}`} title="拖动排序" {...dragHandleListeners} {...dragHandleAttributes} />
            <div className="main_item_wrap__inner">
                <Form.Item className="question-item" key={item.id}>
                    <div className="question-action">
                        <div>
                            {item.questionTypeId > 6 && item.questionTypeId !== 8 && item.questionTypeId !== 9 && item.questionTypeId !== 10 && item.questionTypeId !== 11 && item.questionTypeId !== 12 && item.questionTypeId !== 13 ? (
                                <Tooltip placement="topLeft" title="本题不支持切换" arrowPointAtCenter>
                                    <Select defaultValue={item.questionTypeId ? item.questionTypeId : null} style={{ width: 110 }} size={'small'} onChange={value => handleChange(value, item)} options={[{ value: 7, label: '地区' }]} disabled getPopupContainer={triggerNode => triggerNode.parentElement} />
                                </Tooltip>
                            ) : (
                                <Select defaultValue={item.questionTypeId ? item.questionTypeId : null} style={{ width: 110 }} size={'small'} onChange={value => handleChange(value, item)} options={typechange} getPopupContainer={triggerNode => triggerNode.parentElement} />
                            )}

                            <Checkbox className="question-require" onChange={e => onRequire(e, item)} checked={item.required}>
                                必答
                            </Checkbox>

                            {location.state.route !== 'newCreate'
                                && (item.questionTypeId === 1 ? (
                                    <span className="drop-item" onClick={() => goLogic()} onMouseDown={e => e.stopPropagation()}>
                                        <i className="bi bi-diagram-3"></i> 逻辑
                                    </span>
                                ) : item.questionTypeId === 3 ? (
                                    <span className="drop-item" onClick={() => goLogic()} onMouseDown={e => e.stopPropagation()}>
                                        <i className="bi bi-diagram-3"></i> 逻辑
                                    </span>
                                ) : item.questionTypeId === 8 ? (
                                    <span className="drop-item" onClick={() => goLogic()} onMouseDown={e => e.stopPropagation()}>
                                        <i className="bi bi-diagram-3"></i> 逻辑
                                    </span>
                                ) : null)}

                            {item.questionTypeId === 13 && (
                                <div className="select-question-switch">
                                    支持多选:
                                    <Switch size="small" checkedChildren="开" unCheckedChildren="关" onChange={value => onSelectMultipleChoice(value, item)} defaultChecked={item.isMultipleChoice === 0 || item.isMultipleChoice === null ? false : true} />
                                </div>
                            )}

                            {(item.questionTypeId === 2 || (item.questionTypeId === 13 && item?.isMultipleChoice === 1)) && (
                                <div className="select_range">
                                    <span style={{ paddingRight: 4 }}>可选范围：最少</span>
                                    <Select
                                        getPopupContainer={triggerNode => triggerNode.parentElement}
                                        style={{ width: 80 }}
                                        options={changeOpinion('min', item.options, item.maxSelect)}
                                        size={'small'}
                                        onSelect={(key, value) => onChangeSelect('min', key, value, item)}
                                        defaultValue={item.minSelect === 0 || item.minSelect === null ? '不限' : `${item.minSelect}项`}
                                        onMouseDown={e => e.stopPropagation()}
                                    />
                                    <span style={{ paddingLeft: 8, paddingRight: 4 }}>最多</span>
                                    <Select
                                        getPopupContainer={triggerNode => triggerNode.parentElement}
                                        style={{ width: 80 }}
                                        options={changeOpinion('max', item.options, item.minSelect)}
                                        size={'small'}
                                        onSelect={(key, value) => onChangeSelect('max', key, value, item)}
                                        defaultValue={item.maxSelect === 0 || item.maxSelect === null ? '不限' : `${item.maxSelect}项`}
                                        onMouseDown={e => e.stopPropagation()}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="qusetion-action-right">
                            {item.questionTypeId !== 12 ? (
                                <div>
                                    <span className="drop-item" onClick={() => onCopy(item)} onMouseDown={e => e.stopPropagation()}>
                                        <i className="bi bi-copy"></i>
                                        复制
                                    </span>
                                </div>
                            ) : null}
                            <div>
                                <span className="drop-item" onClick={() => onDelete(item.id)} onMouseDown={e => e.stopPropagation()}>
                                    <i className="bi bi-trash"></i>
                                    删除
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="question-body-area">
                        <QuestionContent item={item} anserArea={anserArea} setAnserArea={setAnserArea} logicList={logicList} onReset={onReset} selectQuestionItem={selectQuestionItem} moreRef={moreRef} start={start} end={end} />
                    </div>
                </Form.Item>
            </div>
        </div>
    );
});

const SortableQuestionCard = memo(function SortableQuestionCard({ item, index, addId, ...cardProps }) {
    const sortableId = getQuestionItemKey(item, index);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
        /* 关闭索引变化时的布局补偿动画，避免与 scale 组合导致题干区视觉变形 */
        animateLayoutChanges: () => false,
    });
    const style = {
        transform: CSS.Transform.toString(normalizeSortableTransform(transform)),
        transition,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    };
    const isSelected = addId != null && item?.id != null && String(addId) === String(item.id);
    return (
        <div ref={setNodeRef} style={style} className="sortable-question-card">
            <QuestionCard {...cardProps} item={item} dragHandleListeners={listeners} dragHandleAttributes={attributes} isDragging={isDragging} isSelected={isSelected} />
        </div>
    );
});

const Topic = ({ anserArea, setAnserArea }, ref) => {
    const { notifySaved } = useSurveyAutosave();
    const { Content, Sider } = Layout;
    const [form] = Form.useForm();
    const [action, setAction] = useState(undefined);
    const [titleBorder, setTitleBorder] = useState(false);
    const [titleIn, setTitleIn] = useState(false);
    //问卷标题
    const [surveyTitle, setSurveyTitle] = useState(undefined);
    //问卷介绍
    const [surveyDirections, setSurveyDirections] = useState(undefined);
    //问卷状态
    const [status, setStatus] = useState(undefined);

    //逻辑数据
    const [logicList, setLogicList] = useState(undefined);

    const [minOpinion, setMinOpinion] = useState(undefined);
    const [maxOpinion, setMaxOpinion] = useState(undefined);
    const [minDefault, setMinDefault] = useState(undefined);
    const [maxDefault, setMaxDefault] = useState(undefined);
    const [addId, setAddId] = useState(undefined);
    /** 标题 / 简介区域点击选中描边（与题目 addId 互斥） */
    const [selectedMeta, setSelectedMeta] = useState(null);
    const { TextArea } = Input;
    const moreRef = useRef(null);
    const start = moreRef.current?.seleteStart;
    const end = moreRef.current?.seleteEnd;
    const location = useLocation();
    const navigate = useNavigate();

    //创建题目
    const questionItem = (type, it) => {
        const sign = anserArea.some(it => it.questionTypeId === 12);
        if (sign && type === 12) return message.warning('已经有电子签名，请勿重复添加');
        else {
            if (addId) {
                insertQuestion(type, it);
            } else {
                newQuestion(type, it);
            }
        }
    };

    const newQuestion = (type, it) => {
        let question;
        if (it) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: it.questionTypeId,
                remark: false,
                isMultipleChoice: it.isMultipleChoice,
                required: it.required,
                maxSelect: it?.maxSelect,
                minSelect: it?.minSelect,
                body: it.body,
                options: it.questionOptionsList,
                weight: anserArea.length + 1,
            };
        } else if (type === 8) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                required: 0,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                body: '请输入问题',
                options: [
                    { id: 0, content: '1', isBlank: 0 },
                    { id: 1, content: '2', isBlank: 0 },
                    { id: 2, content: '3', isBlank: 0 },
                    { id: 3, content: '4', isBlank: 0 },
                    { id: 4, content: '5', isBlank: 0 },
                ],
                weight: anserArea.length + 1,
            };
        } else if (type === 10) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                required: 0,
                body: '请输入问题',
                options: [
                    {
                        id: 0,
                        content: JSON.stringify([
                            ['列标题1', '列标题2'],
                            ['矩阵行1', '矩阵行2'],
                        ]),
                    },
                ],
                weight: anserArea.length + 1,
            };
        } else if (type === 13) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                required: 0,
                body: '请输入问题',
                options: [
                    { id: 0, content: '选项1', isBlank: 0 },
                    { id: 1, content: '选项2', isBlank: 0 },
                ],
                weight: anserArea.length + 1,
            };
        } else {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                required: 0,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                body: '请输入问题',
                options:
                    type === 3
                        ? [
                              { content: '对', isBlank: 0 },
                              { content: '错', isBlank: 0 },
                          ]
                        : [
                              { id: 0, content: '选项1', isBlank: 0 },
                              { id: 1, content: '选项2', isBlank: 0 },
                          ],
                weight: anserArea.length + 1,
            };
        }
        if (location.state.route === 'newCreate') {
            saveTemplateQuestionApi({
                ...question,
                templateId: location.state.id,
            }).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
            setAnserArea([...anserArea, question]);
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            saveSurveryQuestionApi({
                ...question,
                surveyId: location.state.id,
            }).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
            setAnserArea([...anserArea, question]);
        }
    };

    const insertQuestion = (type, it) => {
        console.log(addId);
        const insertIndex = anserArea.findIndex(it => it.id === addId);

        const idArr = anserArea.map(item => Number(item.id));
        let question;
        if (it) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: it.questionTypeId,
                remark: false,
                required: it.required,
                isMultipleChoice: it.isMultipleChoice,
                maxSelect: it?.maxSelect,
                minSelect: it?.minSelect,
                body: it.body,
                options: it.questionOptionsList,
                weight: anserArea[insertIndex]?.weight,
            };
        } else if (type === 8) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                required: 0,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                body: '请输入问题',
                options: [
                    { id: 0, content: '1', isBlank: 0 },
                    { id: 1, content: '2', isBlank: 0 },
                    { id: 2, content: '3', isBlank: 0 },
                    { id: 3, content: '4', isBlank: 0 },
                    { id: 4, content: '5', isBlank: 0 },
                ],
                weight: anserArea.length + 1,
            };
        } else if (type === 10) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                required: 0,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                body: '请输入问题',
                options: [
                    {
                        id: 0,
                        content: JSON.stringify([
                            ['列标题1', '列标题2'],
                            ['矩阵行1', '矩阵行2'],
                        ]),
                    },
                ],
                weight: anserArea.length + 1,
            };
        } else if (type === 13) {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                required: 0,
                body: '请输入问题',
                options: [
                    { id: 0, content: '选项1', isBlank: 0 },
                    { id: 1, content: '选项2', isBlank: 0 },
                ],
                weight: anserArea.length + 1,
            };
        } else {
            question = {
                templateName: surveyTitle,
                directions: surveyDirections,
                questionTypeId: type,
                remark: false,
                required: 0,
                isMultipleChoice: 0,
                maxSelect: 0,
                minSelect: 0,
                body: '请输入问题',
                weight: anserArea[insertIndex]?.weight,
                options:
                    type === 3
                        ? [
                              { content: '对', isBlank: 0 },
                              { content: '错', isBlank: 0 },
                          ]
                        : [
                              { id: 0, content: '选项1', isBlank: 0 },
                              { id: 1, content: '选项2', isBlank: 0 },
                          ],
            };
        }
        if (location.state.route === 'newCreate') {
            saveTemplateQuestionApi({
                ...question,
                templateId: location.state.id,
            }).then(res => {
                if (res.status_code === 200) {
                    idArr.splice(insertIndex + 1, 0, res.data.question.id);
                    const newId = res.data.question.id;
                    sortTemplateQuestionApi({ questionIds: idArr }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                            setAddId(newId);
                        }
                    });
                }
            });
            // setAnserArea([...anserArea, question])
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            saveSurveryQuestionApi({
                ...question,
                surveyId: location.state.id,
            }).then(res => {
                if (res.status_code === 200) {
                    idArr.splice(insertIndex + 1, 0, res.data.question.id);
                    const newId = res.data.question.id;
                    sortSurveryQuestionApi({ questionIds: idArr }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                            setAddId(newId);
                        }
                    });
                }
            });
            // setAnserArea([...anserArea, question])
        }
    };
    useEffect(() => {
        getQuestionBankListApi().then(res => {
            if (res.status_code === 200) {
                const arr = res.data.filter(item => item.typeList?.length > 0 || item.questionList?.length > 0);
                setAction([...arr]);
            }
        });

        getAllLogicApi({ surveyId: location.state.id }).then(res => {
            if (res.status_code === 200) {
                setLogicList(res.data);
            }
        });
    }, []);

    const typechange = [
        {
            value: 1,
            label: '单选题',
        },
        {
            value: 2,
            label: '多选题',
        },
        {
            value: 3,
            label: '判断题',
        },
        {
            value: 4,
            label: '填空题',
        },
        {
            value: 5,
            label: '多项填空题',
        },
        {
            value: 6,
            label: '问答题',
        },
        {
            value: 8,
            label: '量表题',
        },
        {
            value: 9,
            label: '排序题',
        },
        {
            value: 10,
            label: '矩形填空',
        },
        {
            value: 11,
            label: '文件收集',
        },
        {
            value: 12,
            label: '电子签名',
        },
        {
            value: 13,
            label: '选择列表',
        },
    ];
    const onReset = () => {
        if (location.state.route === 'newCreate') {
            previewTemplateApi({ id: location.state.id }).then(res => {
                if (res.status_code === 200) {
                    form.setFieldValue('title', res.data.name);
                    form.setFieldValue('introduce', res.data.directions);
                    setSurveyTitle(res.data.name);
                    setSurveyDirections(res.data.directions);
                    setAnserArea(res.data.questions);
                    if (res.data.questions) {
                        setAnserArea(res.data.questions);
                    } else {
                        setAnserArea([]);
                    }
                }
            });
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            previewSurveryApi({ id: location.state.id }).then(res => {
                if (res.status_code === 200) {
                    form.setFieldValue('title', res.data.name);
                    form.setFieldValue('introduce', res.data.directions);
                    setSurveyTitle(res.data.name);
                    setSurveyDirections(res.data.directions);
                    setStatus(res.data.status);
                    if (res.data.questions) {
                        setAnserArea(res.data.questions);
                    } else {
                        setAnserArea([]);
                    }
                }
            });
        }
    };
    useEffect(() => {
        onReset();
    }, []);

    /** 点击主内容区空白或非标题/简介/题目块时，取消全部选中描边 */
    const allDoc = useCallback(e => {
        const el = e.target;
        if (!(el instanceof HTMLElement)) {
            setAddId(undefined);
            setSelectedMeta(null);
            return;
        }
        if (el.closest('.wj-topic-block-title') || el.closest('.wj-topic-block-introduce') || el.closest('.main_item_wrap')) {
            return;
        }
        setAddId(undefined);
        setSelectedMeta(null);
    }, []);
    const onCopy = item => {
        const insertIndex = anserArea.findIndex(it => it.id === item.id);

        const idArr = anserArea.map(item => Number(item.id));
        const sendValue = {
            ...item,
            templateId: location.state.id,
        };
        var insertId;
        if (location.state.route === 'newCreate') {
            saveTemplateQuestionApi(sendValue).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                    idArr.splice(insertIndex + 1, 0, res.data.question.id);

                    sortTemplateQuestionApi({ questionIds: idArr }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                        }
                    });
                }
            });
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            saveSurveryQuestionApi(sendValue).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                    idArr.splice(insertIndex + 1, 0, res.data.question.id);

                    sortSurveryQuestionApi({ questionIds: idArr }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                        }
                    });
                }
            });
        }
    };

    const addItem = (e, item) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
        setAddId(item.id);
        setSelectedMeta(null);
    };

    /** 题干内联编辑器聚焦时同步当前题目选中（避免点击题干时父级 onClick 先 setState 与 TinyMCE 抢焦点导致需点两次） */
    const selectQuestionItem = useCallback(item => {
        setAddId(item.id);
        setSelectedMeta(null);
    }, []);

    const onDelete = key => {
        Modal.confirm({
            title: '提示',
            content: '删除后本题目将不再能找回，确定要删除吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: () => {
                if (location.state.route === 'newCreate') {
                    delTemplateQuestionApi({ id: key }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                        }
                    });
                } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
                    const delIndex = anserArea.findIndex(it => it.id === key);
                    anserArea.splice(delIndex, 1);
                    const idArr = anserArea.map(it => it.id);
                    delSurveryQusetionApi({ id: key }).then(res => {
                        if (res.status_code === 200) {
                            if (idArr.length > 0) {
                                sortSurveryQuestionApi({ questionIds: idArr }).then(res => {
                                    if (res.status_code === 200) {
                                        onReset();
                                    }
                                });
                            } else {
                                onReset();
                            }
                        }
                    });
                }
            },
        });
    };
    //发布&保存
    // const release = () => {
    //     if(!surveyTitle){
    //         message.error('请输入问卷标题')
    //         return false
    //     }else if(!surveyDirections){
    //         message.error('请输入问卷介绍')
    //         return false
    //     }else {
    //         return true
    //     }

    // }
    useImperativeHandle(ref, () => ({
        surveyTitle,
        allDoc,
    }));

    const changeOpinion = (type, len, size) => {
        if (type === 'min') {
            const res = [];
            if (size === null) size = 0;
            const arr = len.map((item, index) => {
                if (index >= size && size !== 0) {
                    res.push({ id: `${index + 1}`, value: `${index + 1}项`, key: `${index + 1}`, disabled: true });
                } else {
                    res.push({ id: `${index + 1}`, value: `${index + 1}项`, key: `${index + 1}` });
                }
                return res;
            });
            res.unshift({
                id: 0,
                key: 0,
                value: '不限',
            });

            return res;
        } else if (type === 'max') {
            const res = [];
            if (size === null) size = 0;
            const arr = len.map((item, index) => {
                if (index + 1 < size) {
                    res.push({ id: `${index + 1}`, value: `${index + 1}项`, key: `${index + 1}`, disabled: true });
                } else {
                    res.push({ id: `${index + 1}`, value: `${index + 1}项`, key: `${index + 1}` });
                }
                return res;
            });
            res.unshift({
                id: 0,
                key: 0,
                value: '不限',
            });

            return res;
        }
    };

    const onChangeSelect = (type, key, value, item) => {
        if (type === 'min') {
            setMinDefault(value.key);
            const obj = { ...item };
            obj.templateId = obj.surveyId;
            obj.minSelect = Number(value.key);
            if (location.state.route === 'newCreate') {
                timesaveTemplateQuestionApi(obj).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        onReset();
                    }
                });
            } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
                timesaveSurveryQuestionApi(obj).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        onReset();
                    }
                });
            }
        } else if (type === 'max') {
            console.log(minOpinion);
            setMaxDefault(value.key);
            const obj = { ...item };
            obj.templateId = obj.surveyId;
            obj.maxSelect = Number(value.key);
            if (location.state.route === 'newCreate') {
                timesaveTemplateQuestionApi(obj).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        onReset();
                    }
                });
            } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
                timesaveSurveryQuestionApi(obj).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        onReset();
                    }
                });
            }
        }
    };

    const onSelectMultipleChoice = (value, item) => {
        console.log(location.state);
        const obj = { ...item };
        obj.isMultipleChoice = value ? 1 : 0;
        obj.maxSelect = 0;
        obj.minSelect = 0;
        obj.templateId = obj.surveyId;
        if (location.state.route === 'newCreate') {
            timesaveTemplateQuestionApi(obj).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                    onReset();
                }
            });
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            timesaveSurveryQuestionApi(obj).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                    onReset();
                }
            });
        }
    };

    const onSortEndHandler = ({ oldIndex, newIndex }) => {
        const sort = arrayMove(anserArea, oldIndex, newIndex);
        setAnserArea(sort);
        const idArr = sort.map(item => Number(item.id));
        if (location.state.route === 'newCreate') {
            sortTemplateQuestionApi({ questionIds: idArr }).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            sortSurveryQuestionApi({ questionIds: idArr, isModify: true }).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
        }
    };

    const questionSortIds = useMemo(() => anserArea.map((item, index) => getQuestionItemKey(item, index)), [anserArea]);

    const sortSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleQuestionDragEnd = event => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const ids = anserArea.map((item, index) => getQuestionItemKey(item, index));
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        onSortEndHandler({ oldIndex, newIndex });
    };

    const onRequire = (e, item) => {
        const req = e.target.checked === true ? 1 : 0;
        const requiredArr = [...anserArea];
        const index = requiredArr.findIndex(it => it.id === item.id);
        requiredArr[index] = {
            ...requiredArr[index],
            templateId: location.state.id,
            required: req,
        };
        if (location.state.route === 'newCreate') {
            timesaveTemplateQuestionApi(requiredArr[index]);
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            timesaveSurveryQuestionApi(requiredArr[index]);
        }
        setAnserArea(requiredArr);
    };

    const handleChange = (value, item) => {
        const sign = anserArea.some(it => it.questionTypeId === 12);
        if (value === 12 && sign) {
            message.warning('已经有电子签名，请勿重复添加');
            onReset();
            return;
        } else {
            let sendValue;
            if (item.questionTypeId > 2) {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                    options: [
                        { content: '选项1', isBlank: 0 },
                        { content: '选项2', isBlank: 0 },
                    ],
                };
            } else {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                };
            }

            if (value === 3) {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                    options: [
                        { content: '对', isBlank: 0 },
                        { content: '错', isBlank: 0 },
                    ],
                };
            }
            if (value === 8) {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                    options: [
                        { id: 0, content: '1', isBlank: 0 },
                        { id: 1, content: '2', isBlank: 0 },
                        { id: 2, content: '3', isBlank: 0 },
                        { id: 3, content: '4', isBlank: 0 },
                        { id: 4, content: '5', isBlank: 0 },
                    ],
                };
            }
            if (value === 9) {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                    options: [
                        { content: '选项1', isBlank: 0 },
                        { content: '选项2', isBlank: 0 },
                    ],
                };
            }
            if (value === 10) {
                sendValue = {
                    ...item,
                    id: item.id,
                    questionTypeId: value,
                    options: [
                        {
                            id: 0,
                            content: JSON.stringify([
                                ['列标题1', '列标题2'],
                                ['矩阵行1', '矩阵行2'],
                            ]),
                        },
                    ],
                };
            }
            if (location.state.route === 'newCreate') {
                console.log(sendValue);
                changeTypeApi(sendValue).then(res => {
                    if (res.status_code === 200) {
                        onReset();
                    }
                });
            } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
                if (status === 0) {
                    changeSurveyTypeApi(sendValue).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                        }
                    });
                } else {
                    subSurveyApi({ surveyId: location.state.id }).then(res => {
                        if (res.status_code === 200) {
                            if (res.data > 0) {
                                Modal.confirm({
                                    title: '提示',
                                    content: (
                                        <span>
                                            问卷已有{res.data}份回收数据，<span style={{ color: 'red' }}>切换题型后，题目相关数据将被清空，无法在统计结果中查看，</span>确定切换吗？
                                        </span>
                                    ),
                                    cancelText: '切换',
                                    okText: '取消',
                                    cancelButtonProps: { type: 'primary' },
                                    okButtonProps: { type: 'default' },
                                    onCancel: () => {
                                        changeSurveyTypeApi(sendValue).then(res => {
                                            if (res.status_code === 200) {
                                                delAnswerApi({ questionId: item.id });
                                                onReset();
                                            }
                                        });
                                    },
                                    onOk: () => {
                                        onReset();
                                    },
                                });
                            } else {
                                changeSurveyTypeApi(sendValue).then(res => {
                                    if (res.status_code === 200) {
                                        delAnswerApi({ questionId: item.id });
                                        onReset();
                                    }
                                });
                            }
                        }
                    });
                }
            }
        }
    };
    const goLogic = () => {
        localStorage.setItem('logic', 'add');
        navigate('/wj/surveys/new/logic', { state: { ...location.state, title: form.current?.surveyTitle } });
    };

    const getBorder = (e, type) => {
        e.stopPropagation();
        if (type === 'title') {
            setTitleBorder(true);
            setTitleIn(true);
            setSelectedMeta('title');
            setAddId(undefined);
        }
    };

    const cancelBorder = type => {
        if (type === 'title') {
            setTitleBorder(false);
            setTitleIn(false);
            const sendValue = {
                templateId: location.state.id,
                templateName: form.getFieldValue('title') === '' ? '问卷标题' : form.getFieldValue('title'),
                directions: form.getFieldValue('introduce'),
            };
            if (location.state.route === 'newCreate') {
                timesaveTemplateQuestionApi(sendValue).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        if (form.getFieldValue('title') === '') {
                            form.setFieldValue('title', '问卷标题');
                        }
                    }
                });
            } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
                timesaveSurveryQuestionApi(sendValue).then(res => {
                    if (res.status_code === 200) {
                        notifySaved();
                        if (form.getFieldValue('title') === '') {
                            form.setFieldValue('title', '问卷标题');
                        }
                    }
                });
            }
            setSurveyTitle(form.getFieldValue('title'));
        }
    };

    /** 问卷介绍：与题干一致使用 inline RichEditor，描边不触发固定工具条显隐 */
    const handleIntroduceEditorFocus = editor => {
        setSelectedMeta('introduce');
        setAddId(undefined);
        const el = editor?.getContainer?.();
        const root = el?.closest?.('.boss-rich-editor') || el;
        if (root) {
            applyQuestionStemFocusBorder(root, { toggleToolbar: false });
        }
    };

    const handleIntroduceEditorBlur = (editor, latestHtml) => {
        const el = editor?.getContainer?.();
        const root = el?.closest?.('.boss-rich-editor') || el;
        if (root) {
            clearQuestionStemFocusBorder(root, { toggleToolbar: false });
        }
        const directions = latestHtml != null ? latestHtml : form.getFieldValue('introduce');
        const sendValue = {
            templateId: location.state.id,
            templateName: form.getFieldValue('title'),
            directions,
        };
        if (location.state.route === 'newCreate') {
            timesaveTemplateQuestionApi(sendValue).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                }
            });
        } else if (location.state.route === 'newList' || location.state?.route === 'newTemplate') {
            timesaveSurveryQuestionApi(sendValue).then(res => {
                if (res.status_code === 200) {
                    notifySaved();
                }
            });
        }
        setSurveyDirections(directions);
    };

    return (
        <Fragment>
            <Sider className="wj-topic-type-sider" theme="light" width={180}>
                <div className="question-button-action vertical-scrollbar">
                    {action?.map((item, index) => {
                        return (
                            <div key={item.id ?? item.name ?? `action-${index}`}>
                                <p className="question-action-title">{item.name}</p>
                                {item?.typeList?.map((it, idx) => {
                                    return (
                                        <Button
                                            key={it.id ?? it.questionType ?? `type-${idx}`}
                                            className="basis-question"
                                            onClick={() => questionItem(it.questionType)}
                                            // onMouseDown={(e) =>
                                            //     e.stopPropagation()
                                            // }
                                        >
                                            {it.name}
                                        </Button>
                                    );
                                })}
                                {item.questionList?.map((it, i) => {
                                    return (
                                        <Button key={it.id ?? it.questionTypeId ?? it.title ?? `question-${i}`} className="basis-question" onClick={() => questionItem(it.questionTypeId, it)} onMouseDown={e => e.stopPropagation()}>
                                            {it.title}
                                        </Button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </Sider>

            <div className="page-route-body" onClick={e => allDoc(e)} onMouseDown={e => e.stopPropagation()}>
                <div className="question-area vertical-scrollbar">
                    <Form form={form}>
                        <div
                            className={`wj-topic-block-title${selectedMeta === 'title' ? ' topic-editor-block-selected' : ''}`}
                            onClick={e => {
                                e.stopPropagation();
                                setSelectedMeta('title');
                                setAddId(undefined);
                            }}
                        >
                            <Form.Item name="title" rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
                                <Input className="title" variant={titleBorder ? 'outlined' : 'borderless'} onFocus={e => getBorder(e, 'title')} onBlur={e => cancelBorder('title')} onMouseDown={e => e.stopPropagation()} placeholder={'请输入标题'} maxLength={50} style={{ border: titleIn ? '1px solid #1a73e8' : '' }} />
                            </Form.Item>
                        </div>

                        <div
                            className={`wj-topic-block-introduce${selectedMeta === 'introduce' ? ' topic-editor-block-selected' : ''}`}
                            onClick={e => {
                                e.stopPropagation();
                                setSelectedMeta('introduce');
                                setAddId(undefined);
                            }}
                        >
                            <Form.Item name={'introduce'} rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
                                <RichEditor id="wj-topic-survey-introduce" mode="simple" inline className="introduce" placeholder="请输入问卷介绍" height={140} onEditorFocus={handleIntroduceEditorFocus} onEditorBlur={handleIntroduceEditorBlur} />
                            </Form.Item>
                        </div>

                        <DndContext sensors={sortSensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd}>
                            <SortableContext items={questionSortIds} strategy={verticalListSortingStrategy}>
                                <div className="main_content_wrap">
                                    {anserArea.length
                                        ? anserArea.map((item, index) => (
                                              <SortableQuestionCard
                                                  key={getQuestionItemKey(item, index)}
                                                  index={index}
                                                  addId={addId}
                                                  item={item}
                                                  anserArea={anserArea}
                                                  setAnserArea={setAnserArea}
                                                  logicList={logicList}
                                                  onReset={onReset}
                                                  onQuestionClick={addItem}
                                                  selectQuestionItem={selectQuestionItem}
                                                  onRequire={onRequire}
                                                  handleChange={handleChange}
                                                  typechange={typechange}
                                                  location={location}
                                                  onSelectMultipleChoice={onSelectMultipleChoice}
                                                  changeOpinion={changeOpinion}
                                                  onChangeSelect={onChangeSelect}
                                                  goLogic={goLogic}
                                                  onCopy={onCopy}
                                                  onDelete={onDelete}
                                                  moreRef={moreRef}
                                                  start={start}
                                                  end={end}
                                              />
                                          ))
                                        : null}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

export default forwardRef(Topic);
